/**
 * PORT (wide screens): four tab screens laid out anew on a phone. A tab fills the window's height, so its texts in
 * fields sized for the original's 12 px (17 px tall, a line of a list, a label beside its value) measured 8.2 CSS px
 * on a 412 CSS px tall phone and 6 on a 320 px one, and the phone's text growth (ui/wide-popups.ts growTexts) had no
 * room to grow them in. With a wide host showing less than a CSS px per stage px (a phone), where the window leaves
 * room:
 *  - the arsenal (menu_arsenal): each item's name and its rank take the whole height of its row (55 px apart, the
 *    icon's 50: two fields of 25), the name as wide as the row's buttons leave room (they show on a chosen row); the
 *    heading over the description 24 px tall;
 *  - the tops (menu_top): the rows' name, level, rating and captures and the columns' headers in fields 26 px tall
 *    round their middles; the player's rating ("Ваш рейтинг: ?", the star and the value) shown larger (a block
 *    scaled round its top left corner) between the sort buttons and the description;
 *  - the achievements (menu_achiev): the rank and the rating bonus with their values, the star and the "?" shown
 *    larger above the grid of achievements;
 *  - the skills (menu_skills): the skills' column (the icons, names, values, "+" buttons, its heading) moved into the
 *    margin beside the window, the skills' descriptions and the note under them as much wider (their lines, broken
 *    by hand in the original, joined into one wrapped paragraph).
 * The texts grow in their new fields as growTexts grows any text (the larger blocks' texts are scaled with them and
 * left alone by it: `$wideTabText`). What the game writes or moves anew is taken as its own and laid out again.
 * Without a phone's wide view everything is where the original has it.
 */
import { Point, flashRuntime } from '@flash';

/** What a text of the larger blocks measures (CSS px) and the largest scale they take (16 px over 12). */
const TEXT_CSS = 11.5;
const BLOCK_MAX = 16 / 12;
/** Room kept from the shown safe area's edge (CSS px), and how far the skills' column moves at most (stage px). */
const EDGE_CSS = 6;
const SKILLS_SHIFT = 120;
/** The skills' descriptions and the note under them (their art's character ids), and the right end of their column. */
const SKILL_TEXTS = new Set([424, 425, 426, 427, 432]);
const SKILLS_COLUMN_RIGHT = 220;
/** The tops' columns' headers (name, rating, level, captures), the player's rating block (its label, star, value). */
const TOP_HEADERS = new Set([277, 278, 279, 280]);
const TOP_RATING = new Set([272, 273, 275]);
/** The tops' description and the frame round it. */
const TOP_NOTE = 276;
const TOP_FRAME = 281;
/** The achievements' rank and rating bonus labels and values, and the star beside the bonus. */
const ACHIEV_BLOCK = new Set([1156, 1157, 1160, 1161, 275]);
/** An arsenal item's buttons (shown on a chosen row) begin here in its row (every frame of weapon_functions). */
const ARSENAL_BUTTONS = 256.9;

/** What a layout gives an object (its original's values for the rest). */
interface Want {
  x?: number;
  y?: number;
  sx?: number;
  sy?: number;
  width?: number;
  height?: number;
  /** A text's hand-broken lines joined into one wrapped paragraph. */
  join?: boolean;
  /** Clipped to the window as the wide screen dressed it (false: it lies beside the window now). */
  clip?: boolean;
}

interface Geo {
  x: number;
  y: number;
  sx: number;
  sy: number;
  width: number;
  height: number;
}

interface Edit {
  /** What the object had from the game. */
  orig: Geo & { clip: boolean; html: string | null; wrap: boolean; reach: number };
  /** What it was given (read back; a difference later is the game's). */
  set: (Geo & { text: string | null }) | null;
  /** Its version then and the layout it was given for (both the same: nothing to do). */
  ver: number;
  key: string;
  /** A text whose lines it joined (its own html given back with the rest). */
  joined: boolean;
}

const PROPS: Array<keyof Geo> = ['x', 'y', 'sx', 'sy', 'width', 'height'];
/** More than the runtime loses of a position or a size it keeps in twips (1/20 px). */
const TWIP = 0.06;

function geoOf(o: any): Geo {
  return { x: o.x, y: o.y, sx: o.scaleX, sy: o.scaleY, width: o.width, height: o.height };
}

/**
 * A text's lines broken by hand (its htmlText: a paragraph a line) joined again into its paragraphs (a blank line
 * stays, between two of them), a space between two lines where the first does not end in one; the blank lines at
 * its end dropped.
 */
export function joinLines(html: string): string {
  const parts = html.split(/<\/P>/i).filter((p) => p.trim() !== '');
  const plain = (p: string) => p.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&[a-z]+;/g, 'x');
  const paras: string[] = [];
  let open = false;
  let prev = '';
  for (const p of parts) {
    const text = plain(p);
    if (!text.trim()) {
      paras.push(p);
      open = false;
      continue;
    }
    if (!open) paras.push(p);
    else paras[paras.length - 1] += (/\s$/.test(prev) || /^\s/.test(text) ? '' : ' ') + p.replace(/^\s*<P[^>]*>/i, '');
    open = true;
    prev = text;
  }
  while (paras.length && !plain(paras[paras.length - 1]).trim()) paras.pop();
  return paras.length ? paras.map((p) => `${p}</P>`).join('') : html;
}

export class WideTabs {
  private readonly edits = new Map<any, Edit>();
  /** The layout of this frame (the scale and the shown safe area). */
  private key = '';
  /** The skills' column and texts of a screen (found again when its children change). */
  private readonly skillParts = new WeakMap<any, { n: number; column: any[]; texts: any[]; first: number }>();

  /** @param stage the stage (cssScale, safeRect) */
  constructor(private readonly stage: any) {}

  /** The tab screens shown (the game's stack: their links and names), laid out for a wide host (`on`). */
  update(screens: Array<{ link: any; name: string }>, on: boolean): void {
    const css = Number(this.stage.cssScale);
    const live = new Set<any>();
    const safe = this.stage.safeRect;
    this.key = `${css}|${safe.x},${safe.y},${safe.width},${safe.height}`;
    if (on && css > 0 && css < 0.99) {
      for (const { link, name } of screens) {
        if (!link || !link.stage) continue;
        if (name === 'arsenal') this.arsenal(link, live);
        else if (name === 'top') this.top(link, css, live);
        else if (name === 'achiev') this.achiev(link, css, live);
        else if (name === 'skills') this.skills(link, css, live);
      }
    }
    for (const [o, e] of this.edits) {
      if (live.has(o)) continue;
      this.restore(o, e);
      this.edits.delete(o);
    }
  }

  private arsenal(link: any, live: Set<any>): void {
    // (the heading over the description: the name of the item chosen, "Арсенал" before)
    const head = link.weapon_name;
    if (head && head.$renderKind === 'text') this.place(head, live, (o) => ({ y: o.y + o.height / 2 - 12, height: 24 }));
    const box = link.arsenal_weapon_container;
    if (!box || typeof box.numChildren !== 'number') return;
    for (let j = 0; j < box.numChildren; j++) {
      const row = box.getChildAt(j);
      const name = row && row.wp_name;
      const rank = row && row.wp_range_and_status;
      if (!name || !rank || name.$renderKind !== 'text' || rank.$renderKind !== 'text') continue;
      this.place(name, live, (o) => ({ y: 0.5, height: 25, width: Math.max(o.width, ARSENAL_BUTTONS - 5 - o.x) }));
      this.place(rank, live, () => ({ y: 25.5, height: 25 }));
    }
  }

  private top(link: any, css: number, live: Set<any>): void {
    const tall = (o: Geo): Want => ({ y: o.y + o.height / 2 - 13, height: 26 });
    for (let n = 1; n <= 10; n++) {
      const row = link['clip_top' + n];
      if (!row) continue;
      // (and the player's id the original keeps beside the window: a mate of the row's texts)
      for (const f of [row.user_name, row.level, row.user_rating, row.ass, row.id_container]) if (f && f.$renderKind === 'text') this.place(f, live, tall);
    }
    const block: any[] = [];
    // (the description's lines, broken by hand, wrapped anew inside the frame round it)
    let frame: any = null;
    for (let i = 0; i < link.numChildren; i++) if (link.getChildAt(i).$charId === TOP_FRAME) frame = link.getChildAt(i).getBounds(link);
    for (let i = 0; i < link.numChildren; i++) {
      const c = link.getChildAt(i);
      if (c.$charId === TOP_NOTE && c.$renderKind === 'text' && frame) this.place(c, live, (g) => ({ join: true, width: Math.min(g.width, frame.x + frame.width - 6 - g.x) }), String(frame.x + frame.width));
      else if (TOP_HEADERS.has(c.$charId) && c.$renderKind === 'text') this.place(c, live, tall);
      // (the star of the rating's sort button is the same art: the block lies below the sort buttons)
      else if ((TOP_RATING.has(c.$charId) || c.name === 'rating') && (this.edits.get(c)?.orig.y ?? c.y) > 260) block.push(c);
    }
    // (its top 4 px higher: the description under it starts right below the larger block)
    this.block(block, css, -4, live);
  }

  private achiev(link: any, css: number, live: Set<any>): void {
    const block: any[] = [];
    for (let i = 0; i < link.numChildren; i++) {
      const c = link.getChildAt(i);
      if (ACHIEV_BLOCK.has(c.$charId) || c.name === 'achiev_range_popup') block.push(c);
    }
    // (6 px higher: the grid of the achievements starts right below the larger block)
    this.block(block, css, -6, live);
  }

  /**
   * Objects shown larger together: scaled round the top left corner of what they cover as the original places them
   * (moved `dy` px), as far as their 12 px text measures TEXT_CSS (at most BLOCK_MAX); their texts are left alone by
   * the phone's text growth.
   */
  private block(objs: any[], css: number, dy: number, live: Set<any>): void {
    if (objs.length < 2) return;
    const k = Math.min(BLOCK_MAX, Math.max(1, TEXT_CSS / (12 * css)));
    let x0 = Infinity;
    let y0 = Infinity;
    for (const o of objs) {
      const g = this.edits.get(o)?.orig ?? geoOf(o);
      x0 = Math.min(x0, g.x);
      y0 = Math.min(y0, g.y);
    }
    for (const o of objs) {
      if (o.$renderKind === 'text') o.$wideTabText = true;
      this.place(o, live, (g) => ({ x: x0 + (g.x - x0) * k, y: y0 + dy + (g.y - y0) * k, sx: g.sx * k, sy: g.sy * k }), `${x0},${y0}`);
    }
  }

  private skills(link: any, css: number, live: Set<any>): void {
    // How far the column moves: into the margin beside the window as far as the shown safe area leaves room.
    const safe = this.stage.safeRect;
    const left = link.globalToLocal(new Point(safe.x + EDGE_CSS / css, safe.y)).x;
    let parts = this.skillParts.get(link);
    if (!parts || parts.n !== link.numChildren) {
      parts = { n: link.numChildren, column: [], texts: [], first: Infinity };
      for (let i = 0; i < link.numChildren; i++) {
        const c = link.getChildAt(i);
        if (typeof c.name === 'string' && c.name.startsWith('$')) continue;
        if (SKILL_TEXTS.has(c.$charId) && c.$renderKind === 'text') {
          parts.texts.push(c);
          continue;
        }
        // (where the original has it; bounds count a hidden object too)
        const e = this.edits.get(c);
        const b = c.getBounds(link);
        const x = e ? b.x - (c.x - e.orig.x) : b.x;
        // (the column's own objects: below the tabs' bar, left of the descriptions; not the panels)
        if (b.y >= 95 && b.y + b.height <= 470 && x + b.width <= SKILLS_COLUMN_RIGHT && b.width > 0) {
          parts.column.push(c);
          parts.first = Math.min(parts.first, x);
        }
      }
      this.skillParts.set(link, parts);
    }
    const { column, texts, first } = parts;
    if (!column.length || !texts.length) return;
    const d = Math.max(0, Math.min(SKILLS_SHIFT, first - left));
    if (!(d > 1)) return;
    for (const c of column) this.place(c, live, (g) => ({ x: g.x - d, clip: false }), String(d));
    // (lines broken by hand wrapped where the original's longest line ended: short of the panel's divider their fields
    // reach over)
    for (const t of texts) {
      this.place(t, live, (g) => {
        const o = this.edits.get(t)!.orig;
        return { x: g.x - d, width: (o.wrap ? g.width : Math.min(g.width, o.reach)) + d, join: true, clip: false };
      }, String(d));
    }
  }

  /** How far a text's lines reach in its field (its own px: the longest line's end and the field's gutter). */
  private static reachOf(tf: any): number {
    let r = 0;
    for (let l = 0; l < tf.numLines; l++) {
      const m = tf.getLineMetrics(l);
      r = Math.max(r, m.x + m.width);
    }
    return r > 0 ? r + 2 : tf.width;
  }

  /**
   * Gives `o` what `want` asks of its original (the game's values, taken anew where the game changed them); nothing to
   * do while neither the object (its version) nor the layout (`extra`: what `want` depends on beyond the scale and
   * the shown area) changed since.
   */
  private place(o: any, live: Set<any>, want: (orig: Geo) => Want, extra = ''): void {
    live.add(o);
    let e = this.edits.get(o);
    const key = `${this.key}|${extra}`;
    if (e && e.set && e.ver === o.$version && e.key === key) return;
    const text = o.$renderKind === 'text';
    if (!e) {
      e = { orig: { ...geoOf(o), clip: !!o.$designClip, html: text ? String(o.htmlText) : null, wrap: !!o.wordWrap, reach: text ? WideTabs.reachOf(o) : 0 }, set: null, ver: -1, key: '', joined: false };
      this.edits.set(o, e);
    } else if (e.set) {
      // (what the game set since: its own; a text written anew is its own whole)
      const now = geoOf(o);
      for (const k of PROPS) if (Math.abs(now[k] - e.set[k]) > 1e-6) e.orig[k] = now[k];
      if (text && String(o.text) !== e.set.text) {
        e.orig.html = String(o.htmlText);
        e.orig.wrap = !!o.wordWrap;
        e.orig.reach = WideTabs.reachOf(o);
      }
    }
    const w = want(e.orig);
    if (text && w.join && e.orig.html !== null && (!e.set || String(o.text) !== e.set.text || !o.wordWrap)) {
      o.wordWrap = true;
      const joined = joinLines(e.orig.html);
      e.joined = true;
      if (joined !== e.orig.html) {
        // (the text is in the player's language already: not looked up in the catalogue again)
        const runtime = flashRuntime();
        const localize = runtime.localize;
        runtime.localize = null;
        try {
          o.htmlText = joined;
        } finally {
          runtime.localize = localize;
        }
      }
    }
    // (set only where it differs by more than the runtime keeps of it, twips: a value set again every frame would
    // draw the screen anew every frame)
    const sx = w.sx ?? e.orig.sx;
    const sy = w.sy ?? e.orig.sy;
    if (Math.abs(o.scaleX - sx) > 1e-4) o.scaleX = sx;
    if (Math.abs(o.scaleY - sy) > 1e-4) o.scaleY = sy;
    // (a text's box: only where the layout sets it, at its own scale; a larger block's texts are scaled)
    if (text) {
      const width = w.width ?? (w.sx === undefined ? e.orig.width : null);
      const height = w.height ?? (w.sy === undefined ? e.orig.height : null);
      if (width !== null && Math.abs(o.width - width) > TWIP) o.width = width;
      if (height !== null && Math.abs(o.height - height) > TWIP) o.height = height;
    }
    const x = w.x ?? e.orig.x;
    const y = w.y ?? e.orig.y;
    if (Math.abs(o.x - x) > TWIP) o.x = x;
    if (Math.abs(o.y - y) > TWIP) o.y = y;
    const clip = w.clip ?? e.orig.clip;
    if (!!o.$designClip !== clip) o.$designClip = clip;
    e.set = { ...geoOf(o), text: text ? String(o.text) : null };
    e.ver = o.$version;
    e.key = key;
  }

  private restore(o: any, e: Edit): void {
    const g = e.orig;
    if (o.$renderKind === 'text') {
      // (its own lines back where it joined them, unless the game wrote the text anew since)
      if (e.joined) {
        if (o.wordWrap !== g.wrap) o.wordWrap = g.wrap;
        if (e.set && g.html !== null && String(o.text) === e.set.text && String(o.htmlText) !== g.html) {
          const runtime = flashRuntime();
          const localize = runtime.localize;
          runtime.localize = null;
          try {
            o.htmlText = g.html;
          } finally {
            runtime.localize = localize;
          }
        }
      }
      delete o.$wideTabText;
    }
    o.scaleX = g.sx;
    o.scaleY = g.sy;
    if (o.$renderKind === 'text') {
      if (Math.abs(o.width - g.width) > 1e-6) o.width = g.width;
      if (Math.abs(o.height - g.height) > 1e-6) o.height = g.height;
    }
    o.x = g.x;
    o.y = g.y;
    o.$designClip = g.clip;
  }
}
